import { Logger } from 'utilities';
import {
  INDEXED_DB_RECOVERY_RETRY_BASE_DELAY_MS,
  INDEXED_DB_RECOVERY_RETRY_MAX_ATTEMPTS,
  INDEXED_DB_RECOVERY_RETRY_MAX_DELAY_MS,
  INDEXED_DB_TIMEOUT,
} from './indexedDBStorageManager.constants';
import type {
  IndexedDBPendingFallback,
  IndexedDBStorageManager,
  IndexedDBStorageManagerOptions,
  IndexedDBStorageReadResult,
  IndexedDBStorageRecord,
  IndexedDBStorageRecoveryListener,
  IndexedDBStorageSettledReadResult,
} from './indexedDBStorageManager.types';
import {
  clearPendingFallback,
  createRemoveFallbackOperation,
  createSetFallbackOperation,
  handleOpenBlocked,
  handleOpenError,
  handleOpenSuccess,
  handleUpgradeNeeded,
  idbRequest,
  idbTransaction,
  readPendingFallback,
  writePendingFallback,
} from './indexedDBStorageManager.utils';

export const createIndexedDBStorageManager = ({
  dbName,
  dbVersion,
  storeName,
  fallbackStorageKey,
  logNamespace,
}: IndexedDBStorageManagerOptions): IndexedDBStorageManager => {
  let dbPromise: Promise<IDBDatabase> | null = null;
  const memoryFallback = new Map<string, IndexedDBPendingFallback>();
  const fallbackReconciliations = new Map<
    string,
    {
      pendingFallback: IndexedDBPendingFallback;
      promise: Promise<void>;
    }
  >();
  const mutationRevisions = new Map<string, number>();
  const operationQueues = new Map<string, Promise<void>>();
  const recoveryListeners = new Map<string, Set<IndexedDBStorageRecoveryListener<unknown>>>();
  const recoveryStates = new Map<
    string,
    {
      attempt: number;
      inFlight: boolean;
      timeoutId?: ReturnType<typeof setTimeout>;
    }
  >();

  const resetDbPromise = (): void => {
    dbPromise = null;
  };

  const openDB = (): Promise<IDBDatabase> => {
    if (dbPromise) {
      return dbPromise;
    }

    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        resetDbPromise();
        reject(new Error('IndexedDB not available'));
        return;
      }

      const req = indexedDB.open(dbName, dbVersion);

      // If the open request neither resolves nor errors (browser bug, resource pressure,
      // or extension interference), reject so callers do not hang indefinitely.
      let timeoutId: ReturnType<typeof setTimeout>;
      const clearOpenTimeout = () => clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        resetDbPromise();
        reject(new Error(`${logNamespace} IndexedDB open timed out`));
      }, INDEXED_DB_TIMEOUT);

      req.onupgradeneeded = event => handleUpgradeNeeded(event, storeName);
      req.onsuccess = event => handleOpenSuccess(event, resolve, clearOpenTimeout, resetDbPromise);
      req.onerror = () => handleOpenError(req.error, reject, clearOpenTimeout, resetDbPromise);
      req.onblocked = () => handleOpenBlocked(logNamespace);
    });

    return dbPromise;
  };

  const fallbackDeps = {
    memoryFallback,
    fallbackStorageKey,
    logNamespace,
  };

  const getMutationRevision = (key: string): number => mutationRevisions.get(key) ?? 0;

  const enqueueOperation = (key: string, operation: () => Promise<void>): Promise<void> => {
    const previousOperation = operationQueues.get(key) ?? Promise.resolve();
    const queuedOperation = previousOperation.catch(() => undefined).then(operation);
    operationQueues.set(key, queuedOperation);

    return queuedOperation.finally(() => {
      if (operationQueues.get(key) === queuedOperation) {
        operationQueues.delete(key);
      }
    });
  };

  const persistRecord = async (db: IDBDatabase, key: string, value: unknown): Promise<void> => {
    const tx = db.transaction(storeName, 'readwrite');
    const request = tx.objectStore(storeName).put({
      key,
      value,
      updatedAt: Date.now(),
    } satisfies IndexedDBStorageRecord);

    await Promise.all([idbRequest(request), idbTransaction(tx)]);
  };

  const removeRecord = async (db: IDBDatabase, key: string): Promise<void> => {
    const tx = db.transaction(storeName, 'readwrite');
    const request = tx.objectStore(storeName).delete(key);

    await Promise.all([idbRequest(request), idbTransaction(tx)]);
  };

  const cancelRecovery = (key: string): void => {
    const recoveryState = recoveryStates.get(key);
    if (recoveryState?.timeoutId !== undefined) {
      clearTimeout(recoveryState.timeoutId);
    }
    recoveryStates.delete(key);
  };

  const notifyRecovery = <T>(key: string, result: IndexedDBStorageSettledReadResult<T>): void => {
    recoveryListeners.get(key)?.forEach(listener => {
      try {
        (listener as IndexedDBStorageRecoveryListener<T>)(result);
      } catch (error) {
        Logger.error(`${logNamespace} recovery listener failed for key "${key}"`, error);
      }
    });
  };

  const completeRecovery = <T>(key: string, result: IndexedDBStorageSettledReadResult<T>): void => {
    const wasRecovering = recoveryStates.has(key);
    cancelRecovery(key);
    if (wasRecovering) {
      Logger.debug(`${logNamespace} IndexedDB recovery completed for key "${key}"`);
      notifyRecovery(key, result);
    }
  };

  const reconcilePendingFallback = (key: string, pendingFallback: IndexedDBPendingFallback): Promise<void> => {
    const existingReconciliation = fallbackReconciliations.get(key);
    if (existingReconciliation?.pendingFallback === pendingFallback) {
      return existingReconciliation.promise;
    }

    Logger.debug(`${logNamespace} fallback active; reconciling key "${key}"`);
    const reconciliationPromise = enqueueOperation(key, async () => {
      const db = await openDB();
      if (pendingFallback.operation.operation === 'set') {
        await persistRecord(db, key, pendingFallback.operation.value);
      } else {
        await removeRecord(db, key);
      }
      clearPendingFallback({ key, pendingFallback, ...fallbackDeps });
    }).finally(() => {
      if (fallbackReconciliations.get(key)?.pendingFallback === pendingFallback) {
        fallbackReconciliations.delete(key);
      }
    });

    fallbackReconciliations.set(key, {
      pendingFallback,
      promise: reconciliationPromise,
    });
    return reconciliationPromise;
  };

  const readResult = async <T>(key: string): Promise<IndexedDBStorageReadResult<T>> => {
    const pendingFallback = readPendingFallback<T>({ key, ...fallbackDeps });

    if (pendingFallback) {
      // The fallback is already authoritative. Reconcile IndexedDB without
      // delaying startup when the browser has IndexedDB blocked or stalled.
      if (!recoveryStates.has(key)) {
        reconcilePendingFallback(key, pendingFallback).catch(error => {
          Logger.debug(`${logNamespace} fallback promotion failed; fallback remains active:`, error);
          scheduleRecovery(key);
        });
      }

      return pendingFallback.operation.operation === 'set'
        ? { status: 'found', value: pendingFallback.operation.value }
        : { status: 'missing' };
    }

    const mutationRevision = getMutationRevision(key);
    try {
      const db = await openDB();
      const tx = db.transaction(storeName, 'readonly');
      const record = await idbRequest<IndexedDBStorageRecord<T> | undefined>(tx.objectStore(storeName).get(key));

      if (mutationRevision !== getMutationRevision(key)) {
        await operationQueues.get(key)?.catch(() => undefined);
        return readResult<T>(key);
      }

      return record === undefined ? { status: 'missing' } : { status: 'found', value: record.value };
    } catch (error) {
      Logger.debug(`${logNamespace} IndexedDB read failed; using fallback:`, error);
      const fallback = readPendingFallback<T>({ key, ...fallbackDeps });
      if (fallback) {
        return fallback.operation.operation === 'set'
          ? { status: 'found', value: fallback.operation.value }
          : { status: 'missing' };
      }
      return { error, status: 'unavailable' };
    }
  };

  function scheduleRecovery(key: string): void {
    const existingState = recoveryStates.get(key);
    if (existingState?.inFlight || existingState?.timeoutId !== undefined) {
      return;
    }

    const recoveryState = existingState ?? { attempt: 0, inFlight: false };
    if (recoveryState.attempt >= INDEXED_DB_RECOVERY_RETRY_MAX_ATTEMPTS) {
      recoveryStates.delete(key);
      Logger.debug(`${logNamespace} IndexedDB recovery exhausted for key "${key}"`);
      return;
    }

    const retryDelay = Math.min(
      INDEXED_DB_RECOVERY_RETRY_BASE_DELAY_MS * 2 ** recoveryState.attempt,
      INDEXED_DB_RECOVERY_RETRY_MAX_DELAY_MS
    );
    recoveryState.timeoutId = setTimeout(async () => {
      if (recoveryStates.get(key) !== recoveryState) {
        return;
      }

      recoveryState.timeoutId = undefined;
      recoveryState.inFlight = true;
      const pendingFallback = readPendingFallback({ key, ...fallbackDeps });
      let result: IndexedDBStorageReadResult<unknown>;
      try {
        if (pendingFallback) {
          await reconcilePendingFallback(key, pendingFallback);
        }
        result = readPendingFallback({ key, ...fallbackDeps })
          ? { error: new Error('IndexedDB fallback is still active'), status: 'unavailable' }
          : await readResult<unknown>(key);
      } catch (error) {
        Logger.debug(`${logNamespace} fallback promotion failed; fallback remains active:`, error);
        result = { error, status: 'unavailable' };
      }
      if (recoveryStates.get(key) !== recoveryState) {
        return;
      }

      recoveryState.inFlight = false;
      if (result.status === 'unavailable') {
        recoveryState.attempt += 1;
        scheduleRecovery(key);
        return;
      }

      completeRecovery(key, result);
    }, retryDelay);
    recoveryStates.set(key, recoveryState);
  }

  const getResult = async <T>(key: string): Promise<IndexedDBStorageReadResult<T>> => {
    const result = await readResult<T>(key);
    if (result.status === 'unavailable') {
      scheduleRecovery(key);
    } else if (!readPendingFallback({ key, ...fallbackDeps })) {
      completeRecovery(key, result);
    }
    return result;
  };

  const markMutation = (key: string): void => {
    mutationRevisions.set(key, getMutationRevision(key) + 1);
    cancelRecovery(key);
  };

  return {
    get: async <T>(key: string) => {
      const result = await getResult<T>(key);
      return result.status === 'found' ? result.value : undefined;
    },
    getResult,
    subscribeToRecovery: <T>(key: string, listener: IndexedDBStorageRecoveryListener<T>) => {
      const listeners = recoveryListeners.get(key) ?? new Set<IndexedDBStorageRecoveryListener<unknown>>();
      listeners.add(listener as IndexedDBStorageRecoveryListener<unknown>);
      recoveryListeners.set(key, listeners);

      return () => {
        listeners.delete(listener as IndexedDBStorageRecoveryListener<unknown>);
        if (listeners.size === 0) {
          recoveryListeners.delete(key);
        }
      };
    },
    set: async (key: string, value: unknown) => {
      markMutation(key);
      const pendingFallback = writePendingFallback({
        key,
        operation: createSetFallbackOperation(value),
        ...fallbackDeps,
      });

      await enqueueOperation(key, async () => {
        try {
          const db = await openDB();
          await persistRecord(db, key, value);
          clearPendingFallback({ key, pendingFallback, ...fallbackDeps });
          cancelRecovery(key);
        } catch (error) {
          Logger.debug(`${logNamespace} IndexedDB write failed; fallback remains active:`, error);
          scheduleRecovery(key);
        }
      });
    },
    remove: async (key: string) => {
      markMutation(key);
      const pendingFallback = writePendingFallback({
        key,
        operation: createRemoveFallbackOperation(),
        ...fallbackDeps,
      });

      await enqueueOperation(key, async () => {
        try {
          const db = await openDB();
          await removeRecord(db, key);
          clearPendingFallback({ key, pendingFallback, ...fallbackDeps });
          cancelRecovery(key);
        } catch (error) {
          Logger.debug(`${logNamespace} IndexedDB removal failed; fallback remains active:`, error);
          scheduleRecovery(key);
        }
      });
    },
  };
};
